/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 01/28/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import {toInteger} from "../../../../../utils/formValueUtils";

/**
 * HOC for the lms form
 */
class LmsPropertiesForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            lmsPropertyId: null,
            lmsPropertyName: '',
            lmsPropertyValue:'',
            // lms dropdown
            lmsId: '',
            lmsName: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'lmsPropertyName',
                    placeholder: 'Please enter LMS Property Name here',
                    value: '',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Value *',
                    inputName: 'lmsPropertyValue',
                    placeholder: 'Please enter LMS Property value here',
                    value: '',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'LMS *',
                    // initial attribute (the value name of the selected option in the dropdown)
                    inputName: 'lmsId',
                    optionValueName: 'lmsId',
                    optionLabelName: 'lmsName',
                    required: true,
                    path: '/lms-properties',
                    valueType: 'lms'
                }
            ]
        };

        // get the parameter from URL
        const lmsPropertyId = this.props.match.params.id;

        // set icons to the header
        if (lmsPropertyId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lms-properties',
                pageName: "LMS Properties",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lms-properties",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lms-properties',
                pageName: "LMS Properties",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lms-properties",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteLmsProperties);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const lmsPropertyId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create lms property
        if (lmsPropertyId !== undefined) {

            const url = '/lms-properties?search=%7BlmsPropertyId:' + lmsPropertyId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.lmsPropertyId = (data.lmsPropertyId !== undefined) ? data.lmsPropertyId : null;
                    oldState.lmsPropertyName = (data.lmsPropertyName !== undefined) ? data.lmsPropertyName : null;
                    oldState.lmsPropertyValue = (data.lmsPropertyValue !== undefined) ? data.lmsPropertyValue : null;
                    oldState.lmsId = (data.lmsId !== undefined) ? data.lmsId : null;
                    oldState.lmsName = (data.lmsName !== undefined) ? data.lmsName : null;
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteLmsProperties);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel lms properties async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.lmsPropertyId !== null) {
            data = {
                lmsPropertyId: this.state.lmsPropertyId,
                lmsPropertyName: this.state.lmsPropertyName,
                lmsPropertyValue: this.state.lmsPropertyValue,
                lmsId: this.state.lmsId,
                lmsName: this.state.lmsName
            };
        } else {
            data = {
                lmsPropertyName: this.state.lmsPropertyName,
                lmsPropertyValue: this.state.lmsPropertyValue,
                lmsId: toInteger(this.state.lmsId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.lmsPropertyId !== null) {
                this.updateLmsProperty(data);
            } else {
                this.createLmsProperty(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'lmsPropertyName':
                if (value === '')
                    error[key] = "LMS Property Name is Required!";
                else if (value === ' ')
                    error[key] = "LMS Property Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "LMS Property Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'lmsPropertyValue':
                if (value === '')
                    error[key] = "LMS Property Value is Required!";
                else if (value === ' ')
                    error[key] = "LMS Property Value can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 256))
                    error[key] = "LMS Property Value can't have more than 256 characters!";
                else
                    error[key] = "";
                break;
            case 'lmsId':
                if (this.state.lmsId === '')
                    error[key] = "Please select a LMS!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create lms property
     * @param data - the data to create a new lms property
     */
    createLmsProperty = async (data) => {
        await API.post('/lms-properties',
            {
                "lmsPropertyName": data.lmsPropertyName,
                "lmsPropertyValue": data.lmsPropertyValue,
                "lmsId": data.lmsId
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the LMS Property");
            this.props.history.push('/home/lms-properties');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update lms property
     * @param data - updated lms property details
     */
    updateLmsProperty = async (data) => {
        await API.put('/lms-properties',
            {
                "lmsPropertyId": data.lmsPropertyId,
                "lmsPropertyName": data.lmsPropertyName,
                "lmsPropertyValue": data.lmsPropertyValue,
                "lmsId": data.lmsId
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the LMS Property");
            this.props.history.push('/home/lms-properties');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.lmsPropertyId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'lms-properties',
                name: this.state.lmsPropertyName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the LMS Property Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a lms property
     * @param data
     */
    deleteLmsProperties = async (data) => {
        if (data && (this.props.match.path === "/home/lms-properties/:id")) {
            await API.delete('/lms-properties?lmsPropertyId=' + this.state.lmsPropertyId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the LMS Property");
                    this.props.history.push('/home/lms-properties');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "LMS Property is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.lmsPropertyName = "";
        newState.lmsPropertyValue = "";
        newState.lmsId = "";
        newState.lmsName = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/lms-properties');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default LmsPropertiesForm;
